import type { ReactNode } from "react"
import { useState } from "react"
import {
	IconCheckOutline18 as Check,
	IconXmarkOutline18 as X,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { Popover } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"

import { filterOperatorLabel } from "./filter-operator"

export interface FilterChip {
	key: string
	label: string
	middleText?: string
	valueText: string
	/** Number of selected values, drives "is" vs "is any of". */
	valueCount?: number
	isNegated?: boolean
	/** Renders the operator as an is / is not menu when provided. */
	onNegatedChange?: (isNegated: boolean) => void
	onClear: () => void
	popoverContent: ReactNode
	popoverWidthClassName?: string
}

export interface FilterControlChipsProps {
	chips: FilterChip[]
	tailContent?: ReactNode
	containerClassName?: string
}

export function FilterControlChips({
	chips,
	tailContent,
	containerClassName,
}: FilterControlChipsProps) {
	const [openKey, setOpenKey] = useState<string | null>(null)

	if (chips.length === 0 && !tailContent) return null

	return (
		<div
			className={
				containerClassName ??
				"mx-2 my-2 flex cursor-default select-none flex-wrap items-center gap-x-2 gap-y-2 rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 dark:border-gray-800 dark:bg-gray-850"
			}
		>
			{chips.map((chip) => (
				<div
					key={chip.key}
					className="flex items-center divide-x divide-gray-300 overflow-hidden whitespace-nowrap rounded-lg border border-gray-300 bg-white text-xs dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800"
				>
					<span className="px-2 py-1 text-primary">{chip.label}</span>
					{chip.onNegatedChange ? (
						<OperatorMenu
							isNegated={chip.isNegated ?? false}
							valueCount={chip.valueCount}
							onChange={chip.onNegatedChange}
						/>
					) : (
						<span className="px-2 py-1 text-tertiary">
							{chip.middleText ?? "are"}
						</span>
					)}
					<Popover
						className={chip.popoverWidthClassName ?? "w-72 p-0"}
						align="end"
						open={openKey === chip.key}
						onOpenChange={(value) => {
							setOpenKey(value ? chip.key : null)
						}}
						content={chip.popoverContent}
					>
						<button
							type="button"
							className="h-full max-w-24 truncate whitespace-nowrap px-2 py-1 text-xs font-normal text-primary hover:bg-gray-150 dark:hover:bg-gray-750"
						>
							{chip.valueText}
						</button>
					</Popover>
					<button
						type="button"
						className="flex items-center self-stretch px-2 text-tertiary hover:bg-gray-150 hover:text-primary dark:hover:bg-gray-750"
						onClick={chip.onClear}
					>
						<X className="h-3 w-3" />
					</button>
				</div>
			))}
			{tailContent}
		</div>
	)
}

function OperatorMenu({
	isNegated,
	valueCount,
	onChange,
}: {
	isNegated: boolean
	valueCount?: number
	onChange: (isNegated: boolean) => void
}) {
	return (
		<DropdownMenu
			trigger={
				<button
					type="button"
					className="h-full cursor-default whitespace-nowrap px-2 py-1 text-xs font-normal text-tertiary hover:bg-gray-150 hover:text-primary dark:hover:bg-gray-750"
				>
					{filterOperatorLabel(isNegated, valueCount)}
				</button>
			}
			contentProps={{ align: "start" }}
			className="w-36"
		>
			{[false, true].map((negated) => (
				<DropdownMenuItem
					key={negated ? "is-not" : "is"}
					onClick={() => onChange(negated)}
				>
					<span className="flex-1">{negated ? "is not" : "is"}</span>
					<Check
						className={cn("h-4 w-4 flex-none text-secondary", {
							"opacity-0": negated !== isNegated,
						})}
					/>
				</DropdownMenuItem>
			))}
		</DropdownMenu>
	)
}
